import React, { FC, CSSProperties, useEffect, useState } from 'react';
import firstImg from '../components/first.png';
import secondImg from '../components/second.png';
import thirdImg from '../components/third.png';
import fourthImg from '../components/fourth.png';
import fifthImg from '../components/fifth.png';
import { dropdownStyle } from '../components/StyledSelect';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../utils/api';

// --- Type Definitions ---

interface TopInfoItem {
    label: string;
    value: string;
}

interface TableRow {
    id: number;
    branch: string;
    depth: string;
    azimuth: string;
    zenith: string;
    nh: string;
    nl: string;
    po: string;
    date: string;
    time: string;
    note: string;
}

interface TrajectoryDataPoint {
    name: number;
  plan: number | null;
  fact: number | null;
}

interface TrajectoryChartProps {
    title: string;
    data: TrajectoryDataPoint[];
}

// --- Modal Component ---
const Modal: FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  contentStyle?: CSSProperties;
}> = ({ title, onClose, children, contentStyle }) => (
  <div style={styles.modalOverlay}>
    <div style={{ ...styles.modalContent, ...contentStyle }}>
      <div style={styles.modalHeader}>
        <h3 style={styles.modalTitle}>{title}</h3>
        <button aria-label="Закрыть" style={styles.modalCloseButton} onClick={onClose}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// --- Mock Data ---

// --- API Types ---

interface WellsParamsResponse {
  code: number;
  message: string;
  well_list: Array<{
    id: string;
    id_int?: string;
    name: string;
    incline_name?: string;
    mine_name?: string;
    deposit_name?: string;
    station_name?: string;
    contractor_name?: string;
  }>;
}

interface WellsDataResponse {
  code: number;
  message: string;
  trajectory_point_list: Array<{
    id: string;
    branch?: string;
    branch_id?: number | string;
    measurement_depth?: string;
    true_vertical_depth?: string;
    azimuth?: string;
    inclination?: string;
    north_coordinate?: string;
    east_coordinate?: string;
    description?: string;
    point_type?: string;
    measured_at?: string;
    depth?: string;
    dtrack?: string;
    u_d?: string;
    l_r?: string;
    northing?: string;
    easting?: string;
    vertical_displacement?: string;
    tlf_offset?: string;
    comment?: string;
    shot_id?: number | string;
  }>;
  geophysics_gamma_list?: Array<{
    id: string;
    branch: string;
    depth: string;
    azimuth: string;
    inclination: string;
    measured_at: string;
    gamma_1: number;
    gamma_2: number;
    description?: string;
  }>;
}

// --- Helpers ---

const toNum = (s: string | number | undefined | null, def = 0) => {
  if (typeof s === 'number') return s;
  if (typeof s === 'string') {
    const n = Number(String(s).replace(',', '.'));
    return Number.isFinite(n) ? n : def;
  }
  return def;
};

type GeophysicsPoint = { x: number; ГК: number };


// --- Components ---

interface TopInfoBarProps {
  wells: Array<{ id: string; name: string }>;
  selectedWellId?: string;
  onSelectWell: (wellId: string) => void;
  infoItems: TopInfoItem[];
  onUpload?: () => void;
}

const TopInfoBar: FC<TopInfoBarProps> = ({ wells, selectedWellId, onSelectWell, infoItems, onUpload }) => {
    return (
        <div style={styles.topInfoBar}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 24, flex: 1 }}>
                {infoItems.map(item => (
                    <div key={item.label}>
                        <div style={{ fontSize: 12, color: '#aaa' }}>{item.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
                    </div>
                ))}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
         <button
           title="Загрузить данные"
           style={styles.uploadButton}
           onClick={onUpload}
         >
           📥
         </button>
         <select
           style={styles.dropdown}
           value={selectedWellId || ''}
           onChange={(e) => onSelectWell(e.target.value)}
         >
          <option value="" disabled>Выберите скважину</option>
          {wells.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
         </select>
            </div>
        </div>
    );
}

const WellTable: FC<{ rows: TableRow[] }> = ({ rows }) => {
    return (
        <div style={styles.widget}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #444' }}>
                        <th style={styles.th}>№</th>
                        <th style={styles.th}>Ветвь</th>
                        <th style={styles.th}>Глубина</th>
                        <th style={styles.th}>Азимут</th>
                        <th style={styles.th}>Зенит</th>
                        <th style={styles.th}>-Н/+В</th>
                        <th style={styles.th}>-Л/+П</th>
                        <th style={styles.th}>ПО</th>
                        <th style={styles.th}>Дата</th>
                        <th style={styles.th}>Время</th>
                        <th style={styles.th}>Примечание</th>
                    </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={styles.td}>{row.id}</td>
                            <td style={styles.td}>{row.branch}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.depth}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.azimuth}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.zenith}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.nh}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.nl}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.po}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.date}</td>
                            <td style={{...styles.td, color: '#aaa'}}>{row.time}</td>
                            <td style={styles.td}>{row.note}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const TIMELINE_SEGMENTS = [
  { img: firstImg, alt: 'Этап 1', w: 25 },
  { img: secondImg, alt: 'Этап 2', w: 15 },
  { img: thirdImg, alt: 'Этап 3', w: 20 },
  { img: fourthImg, alt: 'Этап 4', w: 20 },
  { img: fifthImg, alt: 'Этап 5', w: 20 },
];

const SimpleTimeline: FC = () => {
  const segments = TIMELINE_SEGMENTS; // stable reference
  const EDGE_CROP_PCT = 5;
  const [cropped, setCropped] = useState<typeof TIMELINE_SEGMENTS>(segments);

  useEffect(() => {
    let cancelled = false;
    const promises = segments.map(seg => new Promise<{ img: string; alt: string; w: number }>(resolve => {
      const image = new Image();
      image.src = seg.img;
      image.onload = () => {
        try {
          const cropLeft = Math.round(image.width * (EDGE_CROP_PCT / 100));
          const cropRight = Math.round(image.width * (EDGE_CROP_PCT / 100));
          const cropWidth = Math.max(1, image.width - cropLeft - cropRight);
          const canvas = document.createElement('canvas');
          canvas.width = cropWidth;
          canvas.height = image.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(image, cropLeft, 0, cropWidth, image.height, 0, 0, cropWidth, image.height);
            resolve({ img: canvas.toDataURL(), alt: seg.alt, w: seg.w });
          } else {
            resolve(seg);
          }
        } catch {
          resolve(seg);
        }
      };
      image.onerror = () => resolve(seg);
    }));
    Promise.all(promises).then(list => { if (!cancelled) setCropped(list); });
    return () => { cancelled = true; };
  }, [segments]);

  return (
    <div style={{ ...styles.widget, minHeight: 'auto', padding: '16px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#aaa', fontSize: 12, marginBottom: 4 }}>
        <span>0</span><span>50</span><span>100</span><span>150</span><span>200</span><span>250</span><span>300</span>
      </div>
      <div style={{ display: 'flex', height: 40, background: '#111', borderRadius: 6, overflow: 'hidden' }}>
        {cropped.map((s, i) => (
          <div key={i} style={{ flexGrow: s.w, flexBasis: 0, position: 'relative', overflow: 'hidden' }} aria-label={s.alt}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${s.img})`,
                backgroundRepeat: 'repeat-x',
                backgroundSize: 'auto 100%',
                backgroundPosition: 'center',
                imageRendering: 'crisp-edges'
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const GeophysicsChart: FC<{ data: GeophysicsPoint[] }> = ({ data }) => {
  return (
    <div style={styles.widget}>
      <h3 style={styles.widgetTitle}>Геофизика</h3>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 12, height: 12, backgroundColor: '#ff6b6b', borderRadius: '50%' }}></div>
            <span style={{ fontSize: 14, color: '#fff' }}>ГК</span>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis dataKey="x" tick={{ fill: "#aaa" }} />
          <YAxis tick={{ fill: "#aaa" }} />
          <Tooltip contentStyle={{ background: "#333", border: "none" }} />
          <Line type="monotone" dataKey="ГК" stroke="#ff6b6b" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const TrajectoryChart: FC<TrajectoryChartProps> = ({ title, data }) => {
  return (
    <div style={styles.widget}>
      <h3 style={styles.widgetTitle}>{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis dataKey="name" tick={{ fill: "#aaa" }} />
          <YAxis tick={{ fill: "#aaa" }} />
          <Tooltip contentStyle={{ background: "#333", border: "none" }} />
          <Legend />
          <Line type="monotone" dataKey="plan" stroke="#ffb347" strokeWidth={2} dot={false} name="План" connectNulls isAnimationActive={false} />
          <Line type="monotone" dataKey="fact" stroke="#00aaff" strokeWidth={2} dot={false} name="Факт" connectNulls isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const WellPage: FC = () => {
  const { user } = useAuth();
  const token = user?.access_token;

  const [wells, setWells] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedWellId, setSelectedWellId] = useState<string | undefined>(undefined);
  const [tableRows, setTableRows] = useState<TableRow[]>([]);
  const [trajectoryUpDownData, setTrajectoryUpDownData] = useState<TrajectoryDataPoint[]>([]);
  const [trajectoryLeftRightData, setTrajectoryLeftRightData] = useState<TrajectoryDataPoint[]>([]);
  const [infoItems, setInfoItems] = useState<TopInfoItem[]>([]);
  const [page, setPage] = useState<number>(0);
  const pageSize = 15;
  const [geophysicsData, setGeophysicsData] = useState<GeophysicsPoint[]>([]);

  const totalPages = Math.max(1, Math.ceil(tableRows.length / pageSize));
  const visiblePageButtons = 3;
  const pageWindowStart = Math.max(0, Math.min(page, totalPages - visiblePageButtons));
  const visiblePageIndexes = Array.from({ length: Math.min(visiblePageButtons, totalPages) }, (_, index) => pageWindowStart + index);

  // Upload modal state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [stations, setStations] = useState<Array<{ id: string; name: string }>>([]);

  const fetchWells = async () => {
    try {
      const [wellsRes, stationsRes] = await Promise.all([
        fetch(apiUrl('/wells-screen/params'), {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }),
        fetch(apiUrl('/stations/'), {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }),
      ]);

      if (!wellsRes.ok) throw new Error(`Wells params failed: ${wellsRes.status}`);
      const data: WellsParamsResponse = await wellsRes.json();
      const list = (data.well_list || []).map(w => ({ id: w.id, name: w.name }));
      setWells(list);

      if (stationsRes.ok) {
        const stationsData = await stationsRes.json();
        const stationList = (stationsData.station_list || []).map((s: any) => ({ id: s.id, name: s.name ?? '' }));
        setStations(stationList);
      }

      if (!selectedWellId && list.length) {
        setSelectedWellId(list[0].id);
        // Set info items from first well
        const w0 = (data.well_list || [])[0];
        setInfoItems([
          { label: 'Подрядчик', value: w0?.contractor_name || '—' },
          { label: 'Шахта', value: w0?.mine_name || '—' },
          { label: 'Расположение', value: w0?.incline_name || '—' },
          { label: 'Скважина', value: w0?.name || '—' },
          { label: 'Буровая', value: w0?.station_name || '—' },
        ]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTrajectory = async (wellId: string) => {
    try {
      const params = new URLSearchParams();
      params.set('well_id', wellId);
      const res = await fetch(`${apiUrl('/wells-screen/data')}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error(`Wells data failed: ${res.status}`);
      const data: WellsDataResponse = await res.json();
      const points = Array.isArray(data.trajectory_point_list) ? data.trajectory_point_list : [];
      const gamma = Array.isArray(data.geophysics_gamma_list) ? data.geophysics_gamma_list : [];

      // Build table rows
      const sorted = [...points].sort((a, b) => {
        const ta = Date.parse(a.measured_at || '');
        const tb = Date.parse(b.measured_at || '');
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      });
      const rows: TableRow[] = sorted.map((p, idx) => {
        const d = new Date(p.measured_at || '');
        const date = Number.isFinite(d.getTime()) ? d.toLocaleDateString('ru-RU') : '—';
        const time = Number.isFinite(d.getTime()) ? d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—';
        return {
          id: idx + 1,
          branch: p.branch || String(p.branch_id ?? '—'),
          depth: p.measurement_depth ?? p.depth ?? '—',
          azimuth: p.azimuth || '—',
          zenith: p.inclination || '—',
          nh: p.north_coordinate || p.northing || '—',
          nl: p.east_coordinate || p.easting || '—',
          po: '0',
          date,
          time,
          note: p.description || p.comment || '—',
        };
      });
      setTableRows(rows);
      setPage(0);

      // Charts data
      const toTime = (s?: string) => {
        const t = s ? Date.parse(s) : NaN;
        return Number.isFinite(t) ? t : 0;
      };
      const planByDepth = new Map<number, { value: number; time: number }>();
      const factByDepth = new Map<number, { value: number; time: number }>();
      const planLRByDepth = new Map<number, { value: number; time: number }>();
      const factLRByDepth = new Map<number, { value: number; time: number }>();
      for (const p of points) {
        const md = toNum(p.measurement_depth ?? p.depth, NaN);
        if (!Number.isFinite(md)) continue;
        const tvdNeg = -toNum(p.true_vertical_depth ?? p.vertical_displacement, NaN);
        const east = toNum(p.east_coordinate ?? p.easting, NaN);
        const time = toTime(p.measured_at);
        const t = String(p.point_type || '').toLowerCase();
        const upsert = (map: Map<number, { value: number; time: number }>, val: number) => {
          const prev = map.get(md);
          if (!prev || time >= prev.time) map.set(md, { value: val, time });
        };
        if (t === 'planned' || t === 'plan') {
          upsert(planByDepth, tvdNeg);
          upsert(planLRByDepth, east);
        } else if (t === 'actual' || t === 'fact') {
          upsert(factByDepth, tvdNeg);
          upsert(factLRByDepth, east);
        } else {
          // Unknown type or new format without point_type — include into both as last-seen point
          upsert(planByDepth, tvdNeg);
          upsert(factByDepth, tvdNeg);
          upsert(planLRByDepth, east);
          upsert(factLRByDepth, east);
        }
      }
      const depths = Array.from(new Set<number>([
        ...Array.from(planByDepth.keys()),
        ...Array.from(factByDepth.keys()),
        ...Array.from(planLRByDepth.keys()),
        ...Array.from(factLRByDepth.keys()),
      ])).sort((a, b) => a - b);

      const upDown: TrajectoryDataPoint[] = depths.map(md => ({
        name: md,
        plan: planByDepth.has(md) ? planByDepth.get(md)!.value : null,
        fact: factByDepth.has(md) ? factByDepth.get(md)!.value : null,
      }));
      const leftRight: TrajectoryDataPoint[] = depths.map(md => ({
        name: md,
        plan: planLRByDepth.has(md) ? planLRByDepth.get(md)!.value : null,
        fact: factLRByDepth.has(md) ? factLRByDepth.get(md)!.value : null,
      }));

      setTrajectoryUpDownData(upDown);
      setTrajectoryLeftRightData(leftRight);

      // Geophysics (Gamma) chart data
      const geoPoints: GeophysicsPoint[] = gamma
        .map(g => {
          const g1 = toNum(g.gamma_1, 0);
          const g2 = toNum(g.gamma_2, 0);
          const avg = (g1 + g2) / 2;
          return { x: toNum(g.depth, NaN), ГК: avg };
        })
        .filter(p => Number.isFinite(p.x))
        .sort((a, b) => a.x - b.x);
      setGeophysicsData(geoPoints);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchWells();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token || !selectedWellId) return;
    fetchTrajectory(selectedWellId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedWellId]);

  const handleSelectWell = (wellId: string) => {
    setSelectedWellId(wellId);
    // Update info items from selected well
    const found = wells.find(w => w.id === wellId);
    if (found) {
      // Need full metadata; refetch params to get all fields for selected well
      // Alternatively, trigger fetchWells to refresh info
      // For precision, we call fetchWells and then update from list
      (async () => {
        try {
          const res = await fetch(apiUrl('/wells-screen/params'), {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': token ? `Bearer ${token}` : '',
            },
          });
          if (!res.ok) throw new Error('params refresh failed');
          const data: WellsParamsResponse = await res.json();
          const full = (data.well_list || []).find(w => w.id === wellId);
          setInfoItems([
            { label: 'Подрядчик', value: full?.contractor_name || '—' },
            { label: 'Шахта', value: full?.mine_name || '—' },
            { label: 'Расположение', value: full?.incline_name || '—' },
            { label: 'Скважина', value: full?.name || '—' },
            { label: 'Буровая', value: full?.station_name || '—' },
          ]);
        } catch (e) {
          console.error(e);
        }
      })();
    }
  };

  return (
    <div style={styles.container}>
      <TopInfoBar wells={wells} selectedWellId={selectedWellId} onSelectWell={handleSelectWell} infoItems={infoItems} onUpload={() => setShowUpload(true)} />
      <div style={styles.mainGrid}>
        <div>
          <WellTable rows={tableRows.slice(page * pageSize, page * pageSize + pageSize)} />
          <div style={styles.paginationBar}>
            {page > 0 && (
              <button
                onClick={() => setPage(page - 1)}
                style={styles.pageButton}
                title="Предыдущая страница"
              >
                ‹
              </button>
            )}
            {pageWindowStart > 0 && (
              <>
                <button
                  onClick={() => setPage(0)}
                  style={styles.pageButton}
                >
                  1
                </button>
                {pageWindowStart > 1 && <span style={styles.paginationEllipsis}>…</span>}
              </>
            )}
            {visiblePageIndexes.map((idx) => (
              <button
                key={idx}
                onClick={() => setPage(idx)}
                style={{
                  ...styles.pageButton,
                  background: idx === page ? '#2E3749' : '#424F67',
                }}
              >
                {idx + 1}
              </button>
            ))}
            {pageWindowStart + visiblePageButtons < totalPages && (
              <>
                {pageWindowStart + visiblePageButtons < totalPages - 1 && <span style={styles.paginationEllipsis}>…</span>}
                <button
                  onClick={() => setPage(totalPages - 1)}
                  style={styles.pageButton}
                >
                  {totalPages}
                </button>
              </>
            )}
            {page < totalPages - 1 && (
              <button
                onClick={() => setPage(page + 1)}
                style={styles.pageButton}
                title="Следующая страница"
              >
                ›
              </button>
            )}
          </div>
        </div>
        <div style={styles.chartsGrid}>
            <TrajectoryChart title="Траектория Верх/Вниз" data={trajectoryUpDownData} />
            <TrajectoryChart title="Траектория Влево/Вправо" data={trajectoryLeftRightData} />
        </div>
      </div>
      <SimpleTimeline />
      <GeophysicsChart data={geophysicsData} />

      {showUpload && (
        <Modal
          title="Импорт данных"
          onClose={() => {
            setShowUpload(false);
            setUploadFile(null);
            setUploadError('');
          }}
          contentStyle={styles.importModal}
        >
          <div style={styles.importModalBody}>
            <label
              style={styles.importDropzone}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                const droppedFile = event.dataTransfer.files?.[0];
                if (droppedFile) {
                  setUploadFile(droppedFile);
                  setUploadError('');
                }
              }}
            >
              <span style={styles.importDropzoneIcon} />
              <span style={styles.importDropzoneText}>{uploadFile ? uploadFile.name : 'Загрузите файл'}</span>
              <input
                type="file"
                accept=".dart"
                style={{ display: 'none' }}
                onChange={(event) => {
                  setUploadFile(event.target.files ? event.target.files[0] : null);
                  setUploadError('');
                }}
              />
            </label>

            <div style={styles.importFormGrid}>
              <div style={styles.importRow}>
                <label style={styles.importLabel}>Скважина</label>
                <select
                  style={styles.importInput}
                  value={selectedWellId || ''}
                  onChange={(event) => setSelectedWellId(event.target.value)}
                >
                  <option value="">Выбрать</option>
                  {wells.map((well) => (
                    <option key={well.id} value={well.id}>{well.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {uploadError && <div style={styles.importError}>{uploadError}</div>}

            <div style={styles.importFooter}>
              <button
                type="button"
                style={styles.importCancelButton}
                onClick={() => {
                  setShowUpload(false);
                  setUploadFile(null);
                  setUploadError('');
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                style={{ ...styles.importSubmitButton, ...(uploading ? styles.importSubmitButtonDisabled : {}) }}
                disabled={uploading}
                onClick={async () => {
                  if (uploading) return;
                  if (!uploadFile) {
                    setUploadError('Выберите dart-файл для импорта.');
                    return;
                  }
                  if (!selectedWellId) {
                    setUploadError('Выберите скважину.');
                    return;
                  }

                  setUploading(true);
                  setUploadError('');

                  try {
                    const form = new FormData();
                    form.append('file', uploadFile);

                    const params = new URLSearchParams();
                    params.set('well_id', selectedWellId);

                    const response = await fetch(apiUrl(`/wells-screen/survey/imports/?${params.toString()}`), {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                      body: form,
                    });

                    const data = await response.json().catch(() => null);

                    if (!response.ok) {
                      throw new Error(data?.message || `Ошибка импорта файла: ${response.status}`);
                    }

                    if (data?.code !== undefined && data.code !== 0) {
                      throw new Error(data?.message || 'API импорта вернул ошибку');
                    }

                    setShowUpload(false);
                    setUploadFile(null);
                    setUploading(false);
                  } catch (e) {
                    const message = e instanceof Error ? e.message : 'Не удалось импортировать файл';
                    setUploadError(message);
                    setUploading(false);
                  }
                }}
              >
                {uploading ? 'Импортируется...' : 'Импортировать'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- Styles ---

const styles: { [key: string]: CSSProperties } = {
  container: {
    padding: 16,
    display: "flex",
    flexDirection: 'column',
    gap: 12,
  },
    topInfoBar: {
      display: 'flex',
      gap: 24,
      padding: '12px 18px',
      background: '#1F2534',
      borderRadius: 16,
      alignItems: 'center',
        color: '#fff',
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D'
  },
  dropdown: dropdownStyle,
  mainGrid: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: 12,
      flex: 1,
  },
  chartsGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
  },
  widget: {
    background: "#1F2534",
    borderRadius: 16,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    color: '#fff',
    border: '1px solid #2E3749',
    boxShadow: '0 2px 16px #0000003D'
  },
  widgetTitle: {
    marginBottom: 12,
    textAlign: "center",
    fontSize: 18,
    fontWeight: 500,
  },
  th: {
    padding: '8px',
    textAlign: 'left',
    fontSize: 12,
    color: '#aaa',
    fontWeight: '500',
    textTransform: 'uppercase'
  },
  td: {
    padding: '8px',
    fontSize: 14,
    color: '#fff'
  },
  paginationBar: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: 8,
    flexWrap: 'wrap',
  },
  pageButton: {
    border: '1px solid #2E3749',
    color: '#fff',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 12,
    cursor: 'pointer' as const,
    minWidth: 32,
    height: 32,
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationEllipsis: {
    color: '#aaa',
    fontSize: 14,
    minWidth: 20,
    textAlign: 'center' as const,
  },

  // Upload button style
  uploadButton: {
    background: 'transparent',
    border: '1px solid #2F3136',
    borderRadius: 8,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 16,
  },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    inset: 0 as any,
    background: 'rgba(6, 10, 18, 0.58)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: '12px',
    width: '520px',
    maxWidth: '90vw',
    padding: '16px',
    color: '#fff',
    boxShadow: '0 2px 16px #0000003D',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
  },
  modalCloseButton: {
    background: 'transparent',
    border: 'none',
    color: '#cfd3dc',
    fontSize: 20,
    cursor: 'pointer',
  },
  importModal: {
    width: '380px',
    maxWidth: 'calc(100vw - 40px)',
    padding: '18px',
    borderRadius: '4px',
    background: '#171D2C',
    border: '1px solid #222C3E',
    boxShadow: '0 18px 50px rgba(0, 0, 0, 0.36)',
  },
  importModalBody: {
    display: 'grid',
    gap: '20px',
  },
  importDropzone: {
    height: '124px',
    border: '1px dashed #344158',
    borderRadius: '3px',
    background: '#2B3548',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    cursor: 'pointer',
    color: '#FFFFFF',
    boxSizing: 'border-box',
  } as CSSProperties,
  importDropzoneIcon: {
    width: '13px',
    height: '13px',
    display: 'inline-block',
    borderRadius: '50%',
    border: '3px solid rgba(154, 165, 184, 0.34)',
    borderTopColor: '#98A4B8',
    boxSizing: 'border-box',
  } as CSSProperties,
  importDropzoneText: {
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: 600,
  },
  importFormGrid: {
    display: 'grid',
    gap: '18px',
  },
  importRow: {
    display: 'grid',
    gridTemplateColumns: '112px 1fr',
    alignItems: 'center',
    gap: '18px',
  },
  importLabel: {
    color: '#8F9BB2',
    fontSize: '12px',
    fontWeight: 600,
  },
  importInput: {
    width: '100%',
    height: '30px',
    padding: '0 10px',
    background: '#2B3548',
    border: '1px solid #3A465C',
    borderRadius: '3px',
    color: '#FFFFFF',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  } as CSSProperties,
  importError: {
    padding: '8px 10px',
    borderRadius: '3px',
    background: 'rgba(239, 68, 68, 0.12)',
    border: '1px solid rgba(239, 68, 68, 0.35)',
    color: '#ff9f9f',
    fontSize: '12px',
    lineHeight: 1.35,
  },
  importFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '6px',
    marginTop: '6px',
  },
  importCancelButton: {
    height: '28px',
    minWidth: '82px',
    padding: '0 14px',
    border: 'none',
    borderRadius: '2px',
    background: '#2A3344',
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  importSubmitButton: {
    height: '28px',
    minWidth: '130px',
    padding: '0 14px',
    border: 'none',
    borderRadius: '2px',
    background: '#1E4F92',
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  importSubmitButtonDisabled: {
    opacity: 0.48,
    cursor: 'not-allowed',
  },
};

export default WellPage;
